import React from 'react';
import Markdown from 'markdown-to-jsx';
import { Box, useTheme } from '@mui/material';  

/**
 * CustomMarkdown component prevents DOM nesting validation errors
 * that occur when Markdown renders p tags nested inside other p tags.
 * 
 * This happens when a Typography component (which renders as a p tag by default)
 * has a Markdown component inside it that also renders paragraphs.
 */
const CustomMarkdown: React.FC<{ children: string }> = ({ children }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Default overrides to prevent DOM nesting issues
  const options = {
    overrides: {
      p: {
        component: Box,
        props: {
          component: 'div',
          sx: {
            mb: 1,
            mt: 1,
            ...theme.typography.body2,
          },
        },
      },
      // Ensure other block elements are rendered properly
      h1: {
        component: Box,
        props: {
          component: 'h1',
          sx: {
            mt: 2,
            mb: 1,
            ...theme.typography.h1,
          },
        },
      },
      h2: {
        component: Box,
        props: {
          component: 'h2',
          sx: {
            mt: 2,
            mb: 1,
            ...theme.typography.h2,
          },
        },
      },
      h3: {
        component: Box,
        props: {
          component: 'h3',
          sx: {
            mt: 1.5,
            mb: 1,
            ...theme.typography.h3,
          },
        },
      },
      h4: {
        component: Box,
        props: {
          component: 'h4',
          sx: {
            mt: 1.5,
            mb: 1,
            ...theme.typography.h4,
          },
        },
      },
      h5: {
        component: Box,
        props: {
          component: 'h5',
          sx: {
            mt: 1.5,
            mb: 1,
            ...theme.typography.h5,
          },
        },
      },
      h6: {
        component: Box,
        props: {
          component: 'h6',
          sx: {
            mt: 1.5,
            mb: 1,
            ...theme.typography.h6,
          },
        },
      },
      ul: {
        component: Box,
        props: {
          component: 'ul',
          sx: {
            pl: 2,
            mb: 1,
            ...theme.typography.body2,
          },
        },
      },
      ol: {
        component: Box,
        props: {
          component: 'ol',
          sx: {
            pl: 2,
            mb: 1,
            ...theme.typography.body2,
          },
        },
      },
      pre: {
        component: Box,
        props: {
          component: 'pre',
          sx: {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            mb: 1,
            fontFamily: theme.typography.fontFamilyMonospace,
            fontSize: '0.8125rem',
            p: 1,
            bgcolor: isDarkMode ? 'grey.800' : 'grey.100',
            color: theme.palette.text.primary,
            borderRadius: '4px',
            overflowX: 'auto',
            maxWidth: '100%',
          },
        },
      },
      code: {
        component: Box,
        props: {
          component: 'code',
          sx: {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            fontFamily: theme.typography.fontFamilyMonospace,
            fontSize: '0.8125rem',
            color: theme.palette.text.primary,
          },
        },
      },
      // Make all links open in new tab
      a: {
        props: {
          target: '_blank',
          rel: 'noopener noreferrer',
          style: {
            color: theme.palette.primary.main,
            textDecoration: 'underline',
            cursor: 'pointer',
          },
        },
      },
    },
  };

  return <Markdown options={options}>{children || ''}</Markdown>;
};

export default CustomMarkdown; 
